import React, { useCallback, useEffect, useRef, useState, useLayoutEffect } from 'react';
import classNames from 'classnames';
import { VARIANTS } from './constants';
import getTabsMeta from './helpers/get-tabs-meta';
import moveTabsScroll from './helpers/move-tabs-scroll';
import TabsScrollButton from '../tabs-scroll-button';
const THRESHOLD = 78;
const SCROLL_BUTTON_WIDTH = 40;

const TabsList = ({
  appearance,
  children,
  variant = VARIANTS.scrollable,
  activeTabManager,
  noBackground = false,
}) => {
  const [isLeftScrollButtonVisible, setLeftScrollButtonVisibility] = useState(false);
  const [isRightScrollButtonVisible, setRightScrollButtonVisibility] = useState(false);
  const tabsRef = useRef(null);
  const isFirstRender = useRef(true);
  const isScrollable = variant === VARIANTS.scrollable;

  const handleLeftScrollButtonClick = () => {
    const { clientWidth } = tabsRef.current;
    moveTabsScroll({ element: tabsRef.current, delta: -clientWidth + THRESHOLD });
  };

  const handleRightScrollButtonClick = () => {
    const { clientWidth } = tabsRef.current;
    moveTabsScroll({ element: tabsRef.current, delta: clientWidth - THRESHOLD });
  };

  const scrollActiveTabIntoView = useCallback((tabs, activeTab) => {
    const tabsMeta = getTabsMeta(tabs);
    const activeTabMeta = activeTab.getBoundingClientRect();

    const scrollBehavior = isFirstRender.current ? 'instant' : 'smooth';
    let delta;

    if (activeTabMeta.left < tabsMeta.left + SCROLL_BUTTON_WIDTH) {
      delta = activeTabMeta.left - tabsMeta.left - SCROLL_BUTTON_WIDTH;
    } else if (activeTabMeta.right > tabsMeta.right - SCROLL_BUTTON_WIDTH) {
      delta = activeTabMeta.right - tabsMeta.right + SCROLL_BUTTON_WIDTH;
    }

    if (delta) {
      const params = { element: tabs, delta, behavior: scrollBehavior };
      moveTabsScroll(params);
    }
  }, []);

  const updateScrollButtonState = useCallback(() => {
    if (!isScrollable) return;
    const { scrollWidth, clientWidth, scrollLeft } = tabsRef.current;
    const shouldLeftScrollButtonBeVisible = scrollLeft > 1;
    const shouldRightScrollButtonBeVisible = scrollLeft < scrollWidth - clientWidth - 1;

    if (shouldLeftScrollButtonBeVisible !== isLeftScrollButtonVisible) {
      setLeftScrollButtonVisibility(shouldLeftScrollButtonBeVisible);
    }
    if (shouldRightScrollButtonBeVisible !== isRightScrollButtonVisible) {
      setRightScrollButtonVisibility(shouldRightScrollButtonBeVisible);
    }
  }, [isScrollable, isLeftScrollButtonVisible, isRightScrollButtonVisible]);

  useEffect(() => {
    updateScrollButtonState();
    window.addEventListener('resize', updateScrollButtonState);
    return () => {
      window.removeEventListener('resize', updateScrollButtonState);
    };
  }, [updateScrollButtonState]);

  /*
   * The useLayoutEffect function is triggered synchronously before the DOM mutations are painted. This is useful since on initial render we need to measure scroll value to active tab before it gets painted.
   * reference: https://reactjs.org/docs/hooks-reference.html#useeffect
   */
  useLayoutEffect(() => {
    const callback = (tabRef) => {
      const activeTab = tabRef?.current;
      const tabs = tabsRef?.current;
      if (activeTab && tabs) {
        scrollActiveTabIntoView(tabs, activeTab);
      }
      isFirstRender.current = false;
    };

    activeTabManager.subscribe(callback);
    return () => {
      activeTabManager.unsubscribe(callback);
    };
  }, [activeTabManager, scrollActiveTabIntoView]);

  const classes = classNames('ws-ui-tabs-list', `ws-ui-tabs-list`, {
    'ws-ui-tabs-list--fullwidth': variant === VARIANTS.fullWidth,
    'ws-ui-tabs-list--scrollable': isScrollable,
    'ws-ui-tabs-list--no-background': noBackground,
  });

  const scrollerClasses = classNames(
    'ws-ui-tabs-list__scroller',
    `ws-ui-tabs-list__scroller--${appearance}`
  );

  return (
    <div className={classes}>
      {isScrollable && isLeftScrollButtonVisible && (
        <div className="ws-ui-tabs-list__scroll-button ws-ui-tabs-list__scroll-button--left">
          <TabsScrollButton
            direction="left"
            onClick={handleLeftScrollButtonClick}
            noBackground={noBackground}
          />
        </div>
      )}
      <div
        className={scrollerClasses}
        onScroll={updateScrollButtonState}
        ref={tabsRef}
        role="tablist"
      >
        {children}
      </div>
      {isScrollable && isRightScrollButtonVisible && (
        <div className="ws-ui-tabs-list__scroll-button ws-ui-tabs-list__scroll-button--right">
          <TabsScrollButton
            direction="right"
            onClick={handleRightScrollButtonClick}
            noBackground={noBackground}
          />
        </div>
      )}
    </div>
  );
};

export default TabsList;
